import { Stack, SimpleGrid, Card, Text, Group, Badge, Table, Box, Alert } from '@mantine/core'
import { IconAlertTriangle, IconUsers, IconShieldLock, IconShieldX, IconChartLine } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import { api } from '@/api/client'
import { useCurrentTenant } from '@/store/auth'
import { EmptyState } from '@/components/ui'
import { EstadoDeConsulta } from '@/components/ui/EstadoDeConsulta'

/**
 * Cada cartao desta tela AFIRMA alguma coisa sobre o mundo quando a lista chega vazia — e as
 * quatro listas vinham de `?? []`, sem nenhum ramo de erro. Com a API fora do ar, uma tela de
 * AUDITORIA dizia "Nenhuma acao foi registrada", "Nenhuma acao sensivel" e — em verde —
 * "Sem negacoes registradas". Silencio de rede virava atestado de seguranca.
 *
 * EstadoDeConsulta garante a ordem carregando -> erro -> vazio -> conteudo, e o estado vazio so
 * aparece depois que a consulta respondeu.
 */
export function AuditoriaDashboard() {
  const tenantId = useCurrentTenant()?.id
  const base = `/v1/tenants/${tenantId}/audit/dashboard`

  const topActors = useQuery({ queryKey: ['dash-actors', tenantId],
    queryFn: async () => (await api.get(`${base}/top-actors?days=7`)).data, enabled: !!tenantId })
  const topActions = useQuery({ queryKey: ['dash-actions', tenantId],
    queryFn: async () => (await api.get(`${base}/top-actions?days=7`)).data, enabled: !!tenantId })
  const denied = useQuery({ queryKey: ['dash-denied', tenantId],
    queryFn: async () => (await api.get(`${base}/denied-rate?days=7`)).data, enabled: !!tenantId })
  const trend = useQuery({ queryKey: ['dash-trend', tenantId],
    queryFn: async () => (await api.get(`${base}/severity-trend?days=30`)).data, enabled: !!tenantId })
  const anomalies = useQuery({ queryKey: ['dash-anom', tenantId],
    queryFn: async () => (await api.get(`${base}/anomalies`)).data, enabled: !!tenantId })

  // Reformatar trend para Recharts (uma linha por severity)
  const trendByDay = (trend.data ?? []).reduce((acc: any, r: any) => {
    const d = r.day
    acc[d] = acc[d] ?? { day: d, info: 0, warning: 0, critical: 0 }
    acc[d][r.severity] = r.count
    return acc
  }, {})
  const trendData = Object.values(trendByDay).sort((a: any, b: any) => a.day.localeCompare(b.day))

  const actorsList = topActors.data ?? []
  const actionsList = topActions.data ?? []
  const deniedList = denied.data ?? []

  return (
    <Stack gap="md">
      {(anomalies.data?.length ?? 0) > 0 && (
        <Alert color="red" icon={<IconAlertTriangle size={20} />}
               title={`${anomalies.data.length} anomalia(s) detectada(s)`}>
          <Stack gap={4}>
            {anomalies.data.map((a: any, i: number) => (
              <Text key={i} size="sm">
                <strong>{a.email}</strong>: {a.last_week_count} eventos esta semana,
                {' '}{a.prev_week_count} na anterior — {a.ratio}× mais
              </Text>
            ))}
          </Stack>
        </Alert>
      )}

      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <Card withBorder>
          <Text fw={600} mb="xs">Top atores (7d)</Text>
          <EstadoDeConsulta
            consulta={topActors}
            vazio={actorsList.length === 0}
            estadoVazio={
              <EmptyState
                bare
                size="sm"
                accent="info"
                icon={IconUsers}
                title="Sem atividade no período"
                description="Nenhuma ação foi registrada nos últimos 7 dias."
              />
            }
          >
            <Table>
              <Table.Tbody>
                {actorsList.map((a: any, i: number) => (
                  <Table.Tr key={i}>
                    <Table.Td><Text size="sm">{a.name || a.email}</Text></Table.Td>
                    <Table.Td><Badge variant="light">{a.event_count}</Badge></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </EstadoDeConsulta>
        </Card>

        <Card withBorder>
          <Text fw={600} mb="xs">Ações sensíveis (7d)</Text>
          <EstadoDeConsulta
            consulta={topActions}
            vazio={actionsList.length === 0}
            estadoVazio={
              <EmptyState
                bare
                size="sm"
                accent="warning"
                icon={IconShieldLock}
                title="Nenhuma ação sensível"
                description="Sem eventos de severidade warning ou critical nos últimos 7 dias."
              />
            }
          >
            <Table>
              <Table.Tbody>
                {actionsList.map((a: any, i: number) => (
                  <Table.Tr key={i}>
                    <Table.Td><Text size="sm" ff="monospace">{a.action}</Text></Table.Td>
                    <Table.Td><Badge color={a.severity === 'critical' ? 'red' : 'yellow'} variant="light">{a.count}</Badge></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </EstadoDeConsulta>
        </Card>

        <Card withBorder>
          <Text fw={600} mb="xs">Taxa de denied por usuário (7d)</Text>
          {/* O pior dos quatro: verde, numa tela de auditoria, afirmando que ninguem teve acao
            negada — quando o que aconteceu foi a consulta nao ter respondido. */}
          <EstadoDeConsulta
            consulta={denied}
            vazio={deniedList.length === 0}
            estadoVazio={
              <EmptyState
                bare
                size="sm"
                accent="success"
                icon={IconShieldX}
                title="Sem negações registradas"
                description="Nenhum usuário teve ações negadas no período."
              />
            }
          >
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Usuário</Table.Th>
                  <Table.Th>Denied</Table.Th>
                  <Table.Th>%</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {deniedList.map((d: any, i: number) => (
                  <Table.Tr key={i}>
                    <Table.Td><Text size="sm">{d.email}</Text></Table.Td>
                    <Table.Td><Text size="sm">{d.denied}/{d.total}</Text></Table.Td>
                    <Table.Td><Badge color="orange" variant="light">{d.denied_pct}%</Badge></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </EstadoDeConsulta>
        </Card>

        <Card withBorder>
          <Text fw={600} mb="xs">Severity trend (30d)</Text>
          <EstadoDeConsulta
            consulta={trend}
            vazio={trendData.length === 0}
            estadoVazio={
              <EmptyState
                bare
                size="sm"
                accent="info"
                icon={IconChartLine}
                title="Sem dados de tendência"
                description="A linha do tempo de severidade aparecerá após 30 dias de eventos."
              />
            }
          >
            <Box h={250}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendData as any}>
                  <XAxis dataKey="day" tickFormatter={(d: string) => d.slice(5)} />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="info"     stroke="#9ca3af" />
                  <Line type="monotone" dataKey="warning"  stroke="#eab308" />
                  <Line type="monotone" dataKey="critical" stroke="#ef4444" />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </EstadoDeConsulta>
        </Card>
      </SimpleGrid>
    </Stack>
  )
}
